/**
 * Speech Service
 * Handles audio recording and speech-to-text transcription
 */

import { apiClient } from './api';

export interface TranscriptionResult {
  transcript: string;
  language: string;
  audio_size_bytes: number;
  extracted_data?: Record<string, any>;
}

export interface AudioRecorder {
  stop: () => Promise<Blob>;
  stream: MediaStream;
}

/**
 * Start recording audio from the user's microphone
 * Returns a recorder object with a stop() method
 * @returns AudioRecorder object with stop method
 */
export const startRecording = async (): Promise<AudioRecorder> => {
  // Request microphone access
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

  // Create MediaRecorder
  const mediaRecorder = new MediaRecorder(stream, {
    mimeType: 'audio/webm;codecs=opus',
  });

  const audioChunks: Blob[] = [];

  // Collect audio data
  mediaRecorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) {
      audioChunks.push(event.data);
    }
  });

  // Start recording
  mediaRecorder.start();

  // Return recorder object with stop method
  return {
    stream,
    stop: () => {
      return new Promise((resolve, reject) => {
        // Handle recording stop
        mediaRecorder.addEventListener('stop', () => {
          // Stop all tracks to release microphone
          stream.getTracks().forEach((track) => track.stop());

          // Create final blob
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
          resolve(audioBlob);
        });

        // Handle errors
        mediaRecorder.addEventListener('error', (event: any) => {
          stream.getTracks().forEach((track) => track.stop());
          reject(new Error(`MediaRecorder error: ${event.error}`));
        });

        // Stop recording
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
        } else {
          reject(new Error('Recorder is not recording'));
        }
      });
    },
  };
};

/**
 * Transcribe audio to text using backend Speech-to-Text API
 * @param audioBlob - Audio blob to transcribe
 * @param language - Language code (es-CL, en-US, pt-BR)
 * @param emptyFields - Array of empty field names for GPT context
 * @param systemPrompt - Custom system prompt for GPT extraction
 * @param fieldOptions - Available options for dropdown fields (currency1, currency2, ourParty, counterparty)
 * @returns Transcription result with optional extracted data
 */
export const transcribeAudio = async (
  audioBlob: Blob,
  language: string = 'es-CL',
  emptyFields?: string[],
  systemPrompt?: string,
  fieldOptions?: Record<string, string[]>
): Promise<TranscriptionResult> => {
  // Create FormData to send audio file
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  formData.append('language', language);

  // Add empty fields for GPT context if provided
  if (emptyFields && emptyFields.length > 0) {
    formData.append('empty_fields', JSON.stringify(emptyFields));
  }

  // Add field options for dynamic prompt generation
  if (fieldOptions) {
    formData.append('field_options', JSON.stringify(fieldOptions));
  }

  // Add custom system prompt if provided
  if (systemPrompt) {
    formData.append('system_prompt', systemPrompt);
  }

  // Send to backend using postFormData for file upload
  const response = await apiClient.postFormData<TranscriptionResult>(
    '/api/v1/speech/transcribe',
    formData
  );

  return response.data;
};

